import { cellInBounds } from '../floor/floor-grid.js';

export interface GridObstacle {
  col: number;
  row: number;
  cols: number;
  rows: number;
}

/** A* en grilla del salón (1×1 por celda caminable). */
export function findSalonPath(
  start: { col: number; row: number },
  goal: { col: number; row: number },
  obstacles: GridObstacle[],
): { col: number; row: number }[] {
  const key = (c: number, r: number) => `${c},${r}`;
  const blocked = new Set<string>();
  for (const o of obstacles) {
    for (let r = o.row; r < o.row + o.rows; r += 1) {
      for (let c = o.col; c < o.col + o.cols; c += 1) {
        blocked.add(key(c, r));
      }
    }
  }
  const startK = key(start.col, start.row);
  const goalK = key(goal.col, goal.row);
  if (blocked.has(startK) || blocked.has(goalK)) return [];

  const open = new Set<string>([startK]);
  const cameFrom = new Map<string, string>();
  const gScore = new Map<string, number>([[startK, 0]]);
  const fScore = new Map<string, number>([[startK, heuristic(start, goal)]]);

  const neighbors = (c: number, r: number) => {
    const out: { col: number; row: number }[] = [];
    for (const [dc, dr] of [[0, 1], [1, 0], [0, -1], [-1, 0]] as const) {
      const nc = c + dc;
      const nr = r + dr;
      if (!cellInBounds('salon', nc, nr, 1, 1)) continue;
      const k = key(nc, nr);
      if (!blocked.has(k)) out.push({ col: nc, row: nr });
    }
    return out;
  };

  while (open.size > 0) {
    let current = '';
    let bestF = Infinity;
    for (const k of open) {
      const f = fScore.get(k) ?? Infinity;
      if (f < bestF) {
        bestF = f;
        current = k;
      }
    }
    if (current === goalK) return reconstruct(cameFrom, current, start);
    open.delete(current);
    const [cc, cr] = current.split(',').map(Number);
    for (const n of neighbors(cc, cr)) {
      const nk = key(n.col, n.row);
      const tentative = (gScore.get(current) ?? Infinity) + 1;
      if (tentative < (gScore.get(nk) ?? Infinity)) {
        cameFrom.set(nk, current);
        gScore.set(nk, tentative);
        fScore.set(nk, tentative + heuristic(n, goal));
        open.add(nk);
      }
    }
  }
  return [];
}

function heuristic(a: { col: number; row: number }, b: { col: number; row: number }): number {
  return Math.abs(a.col - b.col) + Math.abs(a.row - b.row);
}

function reconstruct(
  cameFrom: Map<string, string>,
  current: string,
  start: { col: number; row: number },
): { col: number; row: number }[] {
  const path: { col: number; row: number }[] = [];
  let cur: string | undefined = current;
  while (cur) {
    const [c, r] = cur.split(',').map(Number);
    path.push({ col: c, row: r });
    cur = cameFrom.get(cur);
  }
  path.reverse();
  if (path.length && path[0].col === start.col && path[0].row === start.row) return path;
  return path;
}
